/**
 * syncOfflineService.ts
 * Manages persistent offline storage for pending synchronization items using IndexedDB.
 * This storage survives localStorage.clear() and provides a resilient outbox for exercises.
 */

const DB_NAME = 'LibriTuiSyncDB';
const DB_VERSION = 1;
const STORE_NAME = 'pending_sync';

export interface SyncItem {
    id?: number; // Auto-incrementing key
    original: string;
    translation: string;
    locale: string;
    bookId: string;
    category: string;
    status: 'pending' | 'syncing' | 'failed';
    createdAt: number;
}

class SyncOfflineService {
    private db: IDBDatabase | null = null;

    private async getDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
            };

            request.onsuccess = (event: any) => {
                this.db = event.target.result;
                resolve(this.db!);
            };

            request.onerror = (event: any) => {
                console.error("[SyncOffline] IndexedDB error:", event.target.error);
                reject(event.target.error);
            };
        });
    }

    /**
     * Add multiple items to the persistent sync queue.
     */
    async addItems(items: Omit<SyncItem, 'id' | 'status' | 'createdAt'>[]): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            items.forEach(item => {
                store.add({
                    ...item,
                    status: 'pending',
                    createdAt: Date.now()
                });
            });

            transaction.oncomplete = () => resolve();
            transaction.onerror = (event: any) => reject(event.target.error);
        });
    }

    /**
     * Get all pending items from storage.
     */
    async getAllPending(): Promise<SyncItem[]> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = (event: any) => reject(event.target.error);
        });
    }

    /**
     * Remove an item from the persistent sync queue after successful synchronization.
     * Can be called with a specific numeric ID (preferred/precise) or unique properties (fallback).
     */
    async removeItem(idOrOriginal: number | string, bookId?: string): Promise<void> {
        const db = await this.getDB();
        
        let id: number | undefined;
        if (typeof idOrOriginal === 'number') {
            id = idOrOriginal;
        } else if (bookId) {
            const items = await this.getAllPending();
            const match = items.find(i => i.original === idOrOriginal && i.bookId === bookId);
            if (match) id = match.id;
        }
        
        if (id !== undefined) {
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.delete(id!);

                request.onsuccess = () => resolve();
                request.onerror = (event: any) => reject(event.target.error);
            });
        }
    }

    /**
     * Clear all items (not recommended unless requested).
     */
    async clearAll(): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = (event: any) => reject(event.target.error);
        });
    }
}

export const syncOfflineService = new SyncOfflineService();
